class AyaPurchaseOptions extends HTMLElement {
  connectedCallback() {
    // Re-entrant: also called after `handleVariantUpdate` morphs this element's
    // children, to re-bind against the fresh markup. Only the one-time guard below
    // is skipped on that path (see handleVariantUpdate).
    this.bindRefs();

    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    // Switching Size/Single-vs-3-Pack etc. changes the variant, which changes both
    // prices shown here (one-time price, and the subscription price the discount is
    // off of) — but Horizon's variant-picker only morphs itself on a plain variant
    // change (assets/variant-picker.js, fetchUpdatedSection: morphElementSelector is
    // undefined outside combined listings, so only `updateVariantPicker` runs). This
    // block sits outside that element, so its prices went stale: switching a $79
    // Single to a $209 3-Pack still showed "$79.00" and a subscription price
    // computed off the old variant, even though the correct price was always what
    // actually got charged (server-side cart pricing was never affected — this was
    // a display-only bug).
    //
    // Horizon still announces every variant change via a bubbling `variant:update`
    // event on `document` (assets/events.js) carrying the freshly re-rendered
    // section HTML — the same payload `variant-picker` uses for its own morph. Doing
    // the same thing here, scoped to `<purchase-options>`, keeps this block's prices
    // correct without reimplementing selling-plan price math client-side (percentage
    // vs fixed-amount adjustments, tiers, compare-at — Liquid already computed all of
    // that correctly server-side; duplicating it here would just be a second place
    // for that logic to drift out of sync).
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.handleVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.syncSelectedRow);
    document.removeEventListener('variant:update', this.handleVariantUpdate);
  }

  /** (Re-)acquires element refs and event bindings. Safe to call repeatedly — used
   *  both on first connect and after morphing in a freshly rendered variant. */
  bindRefs() {
    this.removeEventListener('change', this.syncSelectedRow);

    this.subscriptionRadio = this.querySelector('[data-subscription-radio]');
    this.planSelector = this.querySelector('[data-plan-selector]');

    // Mirror the checked radio onto its row as a class. The row highlight used to be
    // driven by `:has(input:checked)` in CSS, but the browser did not repaint the rows
    // when the radio changed — the selector matched the new state while the rendered
    // background stayed on the old row. A class is deterministic; Liquid also stamps
    // the initial one so the correct row is filled before this script runs.
    this.rows = Array.from(this.querySelectorAll('.purchase-options__option'));
    this.syncSelectedRow = this.syncSelectedRow.bind(this);
    this.addEventListener('change', this.syncSelectedRow);
    this.syncSelectedRow();

    if (!this.subscriptionRadio || !this.planSelector) return;

    this.planSelector.addEventListener('change', () => this.updateSubscriptionPlan());
  }

  /**
   * Refreshes this block's prices after Horizon confirms a variant change
   * elsewhere on the page (size swatch, dropdown, etc).
   * @param {CustomEvent} event - Horizon's `variant:update` (assets/events.js)
   */
  async handleVariantUpdate(event) {
    const { data } = event.detail || {};
    if (!data?.html) return;

    // Ignore updates for a different product (e.g. a quick-add dialog for another
    // item elsewhere on the page) — this block only ever represents its own product.
    if (this.dataset.productId && data.productId && this.dataset.productId !== data.productId) {
      return;
    }

    const fresh = data.html.querySelector('purchase-options');
    if (!fresh) return;

    const { morph, MORPH_OPTIONS } = await import('@theme/morph');
    morph(this, fresh, MORPH_OPTIONS);

    // The morph may have replaced (rather than patched) the radios/select this
    // element cached refs to, so re-acquire them against the new markup.
    this.bindRefs();
  }

  /** Puts `is-selected` on whichever option row currently holds the checked radio. */
  syncSelectedRow() {
    for (const row of this.rows) {
      row.classList.toggle('is-selected', !!row.querySelector('input[type="radio"]:checked'));
    }
  }

  updateSubscriptionPlan() {
    const option = this.planSelector.selectedOptions[0];
    if (!option) return;

    this.subscriptionRadio.value = option.value;
    this.subscriptionRadio.checked = true;

    const price = this.querySelector('[data-subscription-price]');
    const compareAt = this.querySelector('[data-subscription-compare-at]');
    const description = this.querySelector('[data-subscription-description]');

    if (price) price.textContent = option.dataset.price || '';
    if (description) description.textContent = option.dataset.description || '';

    if (compareAt) {
      compareAt.textContent = option.dataset.compareAt || '';
      compareAt.hidden = !option.dataset.compareAt;
    }

    this.subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('purchase-options')) {
  customElements.define('purchase-options', AyaPurchaseOptions);
}
